const THEME_HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/** True when value is a full #RRGGBB hex color. */
export function isValidThemeHex(value: string): boolean {
  return THEME_HEX_PATTERN.test(value.trim());
}

/** Normalize to uppercase #RRGGBB; throws if invalid. */
export function normalizeThemeHex(value: string): string {
  const trimmed = value.trim();
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  if (!isValidThemeHex(withHash)) {
    throw new Error(`Invalid theme color: ${value}`);
  }
  return withHash.toUpperCase();
}

/**
 * Convert #RRGGBB to space-separated HSL channels for CSS variables like
 * `--primary: 120 8% 36%` (without the `hsl()` wrapper).
 */
export function hexToHslChannels(hex: string): string | null {
  const match = /^#?([0-9A-Fa-f]{6})$/.exec(hex.trim());
  if (!match) return null;
  const raw = match[1];
  const r = Number.parseInt(raw.slice(0, 2), 16) / 255;
  const g = Number.parseInt(raw.slice(2, 4), 16) / 255;
  const b = Number.parseInt(raw.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = ((g - b) / delta) % 6;
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      default:
        h = (r - g) / delta + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  const hRounded = Math.round(h);
  const sPct = Math.round(s * 100);
  const lPct = Math.round(l * 100);
  return `${hRounded} ${sPct}% ${lPct}%`;
}

function channelLuminance(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function hexChannels(hex: string): [number, number, number] {
  const raw = normalizeThemeHex(hex).slice(1);
  return [
    Number.parseInt(raw.slice(0, 2), 16),
    Number.parseInt(raw.slice(2, 4), 16),
    Number.parseInt(raw.slice(4, 6), 16),
  ];
}

/** WCAG contrast ratio between two #RRGGBB colors. */
export function contrastRatio(a: string, b: string): number {
  const lum = (hex: string) => {
    const [r, g, bl] = hexChannels(hex);
    return (
      0.2126 * channelLuminance(r) +
      0.7152 * channelLuminance(g) +
      0.0722 * channelLuminance(bl)
    );
  };
  const la = lum(a);
  const lb = lum(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Darken a brand color until white text on it meets WCAG AA (4.5:1). Mirrors
 * the backend `accessible_brand_color` used by public jurisdiction pages.
 */
export function accessibleBrandColor(hex: string): string {
  let [r, g, b] = hexChannels(hex);
  for (let i = 0; i < 40; i += 1) {
    const candidate = `#${[r, g, b]
      .map((c) => c.toString(16).padStart(2, "0"))
      .join("")}`.toUpperCase();
    if (contrastRatio(candidate, "#FFFFFF") >= 4.5) return candidate;
    r = Math.floor(r * 0.92);
    g = Math.floor(g * 0.92);
    b = Math.floor(b * 0.92);
  }
  return "#566554";
}

/** Slightly darker channel string for `--accent` when a theme color is set. */
export function hexToDarkerHslChannels(hex: string): string | null {
  const match = /^#?([0-9A-Fa-f]{6})$/.exec(hex.trim());
  if (!match) return null;
  const raw = match[1];
  const r = Math.max(0, Number.parseInt(raw.slice(0, 2), 16) - 20);
  const g = Math.max(0, Number.parseInt(raw.slice(2, 4), 16) - 20);
  const b = Math.max(0, Number.parseInt(raw.slice(4, 6), 16) - 20);
  const darker = `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  return hexToHslChannels(darker);
}
